import { inject, observer } from 'mobx-react'
import React from 'react'
import type { NextPageContext } from 'next'

import { getHomeData } from '../../utils/nextUtils'
import type { LanguageCode } from 'utils/utils'
import { getLocaleStr, codeToLocale, safeStringify } from 'utils/utils'
import { CACHE_POLICY } from '../../utils/constants'

import ContactUs from '../../src/static/pages/ContactUs'
import CustomHead from '../../components/CustomHead'

interface ContactUsDataProps {
	chainId: string
	chainStores: { error: boolean; msg: any }
	lang: string
	eCommerce: boolean
	conversationText: any
	footer: any
	isGeneratedStatically: boolean
	params: any
}

interface ContactUsProps {
	Infra: any
	User: any
	data: ContactUsDataProps
	lang: LanguageCode
}

const ContactUsPage = inject(
	'Infra',
	'User'
)(
	observer(({ Infra, User, data, lang }: ContactUsProps) => {
		const storeLocatorPageText = Infra.appParams?.pageText['contact-us']
		const title = getLocaleStr(storeLocatorPageText?.title, codeToLocale[lang] || 'en_US')
		const description = getLocaleStr(storeLocatorPageText?.description, codeToLocale[lang] || 'en_US')
		const contactUsProps: ContactUsDataProps = {
			chainId: Infra.appParams.c,
			chainStores: data?.chainStores,
			lang: User.preferredLanguage || Infra.appParams.l,
			eCommerce: Infra.appParams.eCommerce,
			conversationText: User.userLocaleLanguage,
			footer: Infra.eCommerceFooter,
			isGeneratedStatically: true,
			params: Infra.appParams,
		}

		// Remove 'ref' from appParams because it throws an error on CustomHead (string ref prop not allowed on function component, see https://legacy.reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
		const { ref, ...appParamsWithoutRef } = Infra.appParams

		return (
			<>
				<CustomHead {...appParamsWithoutRef} title={title} description={description} lang={lang} key={1} />
				<ContactUs {...contactUsProps} />
			</>
		)
	})
)

export async function getServerSideProps(
	context: NextPageContext & { req: { extra: { appParams: any; nextJsError?: string; forwardedUrl: string } } }
) {
	const lang = context.query.lang as string
	const appParams = context.req.extra?.appParams
	const { req, res } = context

	if (req && !req.extra?.nextJsError) {
		try {
			const chainStores = await getHomeData(appParams, req.extra.forwardedUrl, 'getBranchesList', true)

			if (res) {
				res.setHeader(
					'Cache-Control',
					`public, s-maxage=${CACHE_POLICY.DEFAULT.MAX_AGE}, stale-while-revalidate=${CACHE_POLICY.DEFAULT.REVALIDATED_AGE}`
				)
			}

			return {
				props: {
					data: {
						chainStores,
					},
					lang,
				},
			}
		} catch (e: any) {
			console.error(safeStringify(e))
		}
	}

	if (context.res) {
		context.res.statusCode = 500
		context.res.setHeader('Cache-Control', 'no-store')
	}

	return { props: { data: null } }
}

export default ContactUsPage
